import { AnnotationComment, DifferenceTypeNames, Group } from 'types';
import type { BoundingBox, GradeContribution, Location } from '@verify/shared-utils';

export interface AnnotationData {
  location: {
    pageNumber: number;
    rect: Array<number>;
    rotationIndex?: 1 | 2 | 3;
  };
  text: string;
  grid?: string[];
  textFragments?: {
    location: {
      pageNumber: number;
      rect: Array<number>;
      rotationIndex: 1 | 2 | 3;
    };
    text: string;
  }[];
  previousTextLocation?: {
    pageNumber: number;
    rect: Array<number>;
    rotationIndex?: 1 | 2 | 3;
  };
  nextTextLocation?: {
    pageNumber: number;
    rect: Array<number>;
    rotationIndex?: 1 | 2 | 3;
  };
}

export interface AnnotationDetail {
  index: number;
  comments: AnnotationComment[];
  location: Location;
  annotationType: string;
}

export interface DiffToAnnotData extends AnnotationData {
  transformCoord: boolean;
}

type Suggestion = {
  dictionaryName: string;
  suggestions: string[];
};

export type BarcodeMetadataItem = {
  key: string;
  value: string;
  passingStatus?: string;
  /** CTF-provided classification of how the parameter relates to the overall grade. */
  contribution?: GradeContribution;
  /** Recommendation/explanation text (e.g. the overall grade's statusDescription). */
  statusDescription?: string;
};

export type BarcodeDetails = {
  decodedValue: string;
  barcodeMetadata: BarcodeMetadataItem[];
};

export type RgbColor = {
  red: number;
  green: number;
  blue: number;
};

export type CmykColor = {
  cyan: number;
  magenta: number;
  yellow: number;
  black: number;
};

export type LabColor = {
  lStar: number;
  aStar: number;
  bStar: number;
};

export type NamedColorValue = {
  rgb: RgbColor;
  cmyk: CmykColor;
  lab: LabColor;
  name: string;
};

export type ClosestColor = {
  color: NamedColorValue;
  deltaE: number;
};

export const formatRgb = (rgb: RgbColor): string => `R:${rgb.red} G:${rgb.green} B:${rgb.blue}`;

export const formatCmyk = (cmyk: CmykColor): string =>
  `C:${cmyk.cyan} M:${cmyk.magenta} Y:${cmyk.yellow} K:${cmyk.black}`;

export const formatLab = (lab: LabColor): string => `L:${lab.lStar} A:${lab.aStar} B:${lab.bStar}`;

export type ColorDetails = {
  sampledRgb: string;
  sampledCmyk: string;
  sampledLab: string;
  closestRgb: string;
  closestCmyk: string;
  closestLab: string;
  deltaE?: string;
  separationName?: string;
  closestColors?: ClosestColor[];
  selectedClosestColorIndex?: number;
};

type BrailleMeasurement = { key: string; value: number | string; isMarburgCompliant: boolean };

export type BrailleDetails = {
  decodedTextLines: string[];
  dots: DotDetails;
  characters: number;
  decodedTextLinesCount: number;
  brailleMeasurements: BrailleMeasurement[];
};

export interface DotDetails {
  detectedDots: number;
  allDots: AllDotsDetails[];
}
export interface AllDotsDetails {
  isDetected: true;
  boundingBox: BoundingBox;
}
export type IntendedChangeStatus = 'As Intended' | 'Misapplied';

export interface FontMetricsDetails {
  fontPointSize: number;
  contextTag: string;
  isGlobalFontResult?: boolean;
  fontSizeCheckValue: number;
}

export interface TextFragment {
  text: string;
  location: Location;
}

export interface Difference {
  id: string;
  type: DifferenceTypeNames;
  // fix incorrect type
  annotationDetail?: AnnotationDetail;
  displayName: DifferenceTypeNames;
  comment: string;
  viewed: boolean;
  excluded: boolean;
  smartDiscard: boolean;
  groupId: string;
  transformCoord?: boolean; // to know if we will transform their coordinates.
  source: AnnotationData;
  target: AnnotationData;
  description: string; // only for certain differences (what used to be 'extra')
  suggestions?: Suggestion;
  word?: string;
  barcodeDetails?: BarcodeDetails; // only for barcode differences
  brailleDetails?: BrailleDetails; // only for braille differences
  colorDetails?: ColorDetails; // only for color differences
  embeddedImageDetails?: any;
  updatedAt: string;
  matchIndex?: number; // match index
  differenceIndex?: number;
  statusId?: string | null;
  intendedChangeStatus?: IntendedChangeStatus;
  fontMetricsDetails: FontMetricsDetails;
  sourceThumbnailLoading?: boolean;
  targetThumbnailLoading?: boolean;
  resyncId?: number;
  originalDifferenceIds?: string[]; // For merged differences - contains IDs of original differences that were merged
  thumbnailPaths?: {
    [documentType: string]: {
      thumbnailUrl: string | undefined | null; // undefined = loading state, null = failed
    };
  }; // For storing S3-based thumbnails for both merged graphics and markup differences
}

export interface MatchImage {
  path: string;
  dpiUsed?: number;
}

export interface MatchDetail {
  confidence: number;
  masterLocation: Location;
  sampleLocation: Location;
  masterImage?: MatchImage;
  sampleImage?: MatchImage;
}

export interface Match {
  index: number;
  matchDetails: MatchDetail[];
  inputMasterLocation: {
    pageNumber: number;
    boundingBox: BoundingBox;
  };
}

export interface ApiBulkUpdateDifferences {
  differences: Difference[];
  groups: Group[];
  failed: {
    differences: string[];
    groups: string[];
  };
}
export type ApiFetchDifferences = Difference[];

export type FetchDifferencesData = Difference[];

export type ApiRestoreAllDifferences = Difference[];

export type ApiUpdateDifference = Difference;

export type ApiDeleteDifference = Difference;
